import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Icon from 'src/common/components/icons/Icon';
import IconGrid from 'src/common/components/icons/IconGrid';
import IconStyleControls from 'src/common/components/icons/IconStyleControls';
import MaterialIcon from 'src/common/components/icons/MaterialIcon';
import MaterialIconGrid from 'src/common/components/icons/MaterialIconGrid';
import {
  DEFAULT_ICON_STYLE,
  IconStyle,
  buildIconHtml,
  iconStyleColorToSx,
  iconStyleSizeToFontSize,
} from 'src/common/components/icons/iconPicker';
import {
  buildMaterialIconHtml,
  loadMaterialIcon,
  materialIconAttrValue,
  materialIconIdFromAttr,
} from 'src/common/components/icons/materialIconRegistry';
import EditorDialogShell from 'src/common/components/modals/EditorDialogShell';
import EditorFormActions from 'src/common/components/modals/EditorFormActions';

type IconTab = 'custom' | 'material';

interface IconSelectorModalBaseProps {
  open: boolean;
  onClose: () => void;
}

interface IconSelectorHtmlModeProps extends IconSelectorModalBaseProps {
  mode?: 'html';
  onSelect: (iconHtml: string) => void;
}

export interface IconSelectorAppIconModeProps extends IconSelectorModalBaseProps {
  mode: 'appIcon';
  value?: string;
  onSelect: (iconId: string | undefined) => void;
  title?: string;
  showClear?: boolean;
  confirmLabel?: string;
  previewColor?: string;
  previewSize?: string;
}

type IconSelectorModalProps = IconSelectorHtmlModeProps | IconSelectorAppIconModeProps;

const isAppIconMode = (props: IconSelectorModalProps): props is IconSelectorAppIconModeProps =>
  props.mode === 'appIcon';

const IconSelectorModal = (props: IconSelectorModalProps) => {
  const { open, onClose } = props;
  const { t } = useTranslation();
  const appIconMode = isAppIconMode(props);

  const [tab, setTab] = useState<IconTab>('custom');
  const [selectedCustomIcon, setSelectedCustomIcon] = useState('');
  const [selectedMaterialIcon, setSelectedMaterialIcon] = useState('');
  const [iconStyle, setIconStyle] = useState<IconStyle>(DEFAULT_ICON_STYLE);
  const selectedIconValue = appIconMode ? props.value : undefined;

  useEffect(() => {
    if (!open || !appIconMode) return;

    const value = selectedIconValue ?? '';
    const materialId = materialIconIdFromAttr(value);
    if (materialId) {
      setTab('material');
      setSelectedMaterialIcon(materialId);
      setSelectedCustomIcon('');
      return;
    }

    setTab('custom');
    setSelectedCustomIcon(value);
    setSelectedMaterialIcon('');
  }, [open, appIconMode, selectedIconValue]);

  const handleStyleChange = (patch: Partial<IconStyle>) => {
    setIconStyle(prev => ({ ...prev, ...patch }));
  };

  const handleTabChange = (_: unknown, value: IconTab) => {
    setTab(value);
    setSelectedCustomIcon('');
    setSelectedMaterialIcon('');
  };

  const handleMaterialIconSelect = (iconId: string) => {
    setSelectedMaterialIcon(iconId);
    void loadMaterialIcon(iconId);
  };

  const resetState = () => {
    setTab('custom');
    setSelectedCustomIcon('');
    setSelectedMaterialIcon('');
    setIconStyle(DEFAULT_ICON_STYLE);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const hasSelection =
    tab === 'custom' ? Boolean(selectedCustomIcon) : Boolean(selectedMaterialIcon);

  const handleInsert = () => {
    if (appIconMode) {
      if (tab === 'custom' && selectedCustomIcon) {
        props.onSelect(selectedCustomIcon);
        handleClose();
      } else if (tab === 'material' && selectedMaterialIcon) {
        props.onSelect(materialIconAttrValue(selectedMaterialIcon));
        handleClose();
      }
      return;
    }

    const iconHtml =
      tab === 'custom'
        ? buildIconHtml(selectedCustomIcon, iconStyle)
        : selectedMaterialIcon
          ? buildMaterialIconHtml(selectedMaterialIcon, iconStyle)
          : '';
    if (iconHtml) {
      props.onSelect(iconHtml);
      handleClose();
    }
  };

  const handleClear = () => {
    if (appIconMode) {
      props.onSelect(undefined);
      handleClose();
    }
  };

  const previewSx = {
    fontSize: iconStyleSizeToFontSize(iconStyle.size),
    color: iconStyleColorToSx(iconStyle.color),
    margin:
      iconStyle.margin === 'small'
        ? '0.125rem'
        : iconStyle.margin === 'medium'
          ? '0.25rem'
          : iconStyle.margin === 'large'
            ? '0.5rem'
            : 0,
    padding:
      iconStyle.padding === 'small'
        ? '0.125rem'
        : iconStyle.padding === 'medium'
          ? '0.25rem'
          : iconStyle.padding === 'large'
            ? '0.5rem'
            : 0,
  };

  const title = appIconMode
    ? (props.title ?? t('editor.iconSelector.title'))
    : t('editor.iconSelector.title');

  const confirmLabel = appIconMode
    ? (props.confirmLabel ?? t('editor.iconSelector.insert'))
    : t('editor.iconSelector.insert');

  const handleFormSubmit = (event: FormEvent) => {
    event.preventDefault();
    handleInsert();
  };

  const showClearAction = appIconMode && props.showClear;

  return (
    <EditorDialogShell
      id="icon-selector"
      open={open}
      onClose={handleClose}
      title={title}
    >
      <Box component="form" onSubmit={handleFormSubmit}>
        <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab value="custom" label={t('editor.iconSelector.tabs.custom')} />
          <Tab value="material" label={t('editor.iconSelector.tabs.material')} />
        </Tabs>
        {!appIconMode && <IconStyleControls value={iconStyle} onChange={handleStyleChange} />}
        {tab === 'custom' ? (
          <IconGrid
            selectedIcon={selectedCustomIcon}
            onSelect={setSelectedCustomIcon}
            previewColor={appIconMode ? props.previewColor : iconStyle.color}
            previewSize={appIconMode ? props.previewSize : iconStyle.size}
          />
        ) : (
          <MaterialIconGrid
            selectedIcon={selectedMaterialIcon}
            onSelect={handleMaterialIconSelect}
            previewColor={appIconMode ? props.previewColor : iconStyle.color}
            previewSize={appIconMode ? props.previewSize : iconStyle.size}
          />
        )}
        {hasSelection ? (
          <Box sx={{ mt: appIconMode ? 2 : 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('editor.iconSelector.preview')}:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {tab === 'material' && selectedMaterialIcon ? (
                <MaterialIcon
                  iconId={selectedMaterialIcon}
                  sx={appIconMode ? undefined : previewSx}
                />
              ) : (
                <Icon icon={selectedCustomIcon} sx={appIconMode ? undefined : previewSx} />
              )}
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {tab === 'material' && selectedMaterialIcon
                  ? appIconMode
                    ? materialIconAttrValue(selectedMaterialIcon)
                    : `<icon icon="material:${selectedMaterialIcon}" />`
                  : selectedCustomIcon}
              </Typography>
            </Box>
          </Box>
        ) : null}
        <EditorFormActions
          justifyContent={showClearAction ? 'space-between' : 'flex-end'}
          leftSlot={
            showClearAction ? (
              <Button onClick={handleClear} color="inherit">
                {t('system.pages.hero.buttonClearIcon')}
              </Button>
            ) : undefined
          }
          onCancel={handleClose}
          submitLabel={confirmLabel}
          submitDisabled={!hasSelection}
        />
      </Box>
    </EditorDialogShell>
  );
};

export default IconSelectorModal;
